import React, { useState } from "react";
import { AppSidebar } from "@/components/app-sidebar";
import { SiteHeader } from "@/components/site-header";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useUserOrganization } from "@/hooks/useOrganization";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  HelpCircle,
  Search,
  BookOpen,
  Video,
  MessageCircle,
  FileText,
  ExternalLink,
  ChevronRight,
  Star,
  Clock,
  Users,
  Lightbulb,
  AlertCircle,
  CheckCircle,
  Play,
  Download,
  Share2,
  Bookmark,
  ArrowRight,
  Phone,
  Mail,
  MessageSquare,
  ThumbsUp,
} from "lucide-react";

interface HelpArticle {
  id: string;
  title: string;
  description: string;
  category:
    | "getting-started"
    | "policies"
    | "security"
    | "troubleshooting"
    | "advanced";
  difficulty: "beginner" | "intermediate" | "advanced";
  readTime: string;
  tags: string[];
  isPopular?: boolean;
  isNew?: boolean;
}

interface HelpCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  articleCount: number;
  color: string;
}

const helpArticles: HelpArticle[] = [
  {
    id: "1",
    title: "Getting Started with DNS Filtering",
    description:
      "Learn the basics of setting up your first DNS policy and protecting your organization",
    category: "getting-started",
    difficulty: "beginner",
    readTime: "5 min read",
    tags: ["setup", "basics", "first-time"],
    isPopular: true,
  },
  {
    id: "2",
    title: "Creating Your First Policy",
    description:
      "Step-by-step guide to creating and configuring DNS policies for your needs",
    category: "policies",
    difficulty: "beginner",
    readTime: "8 min read",
    tags: ["policies", "configuration", "setup"],
    isPopular: true,
  },
  {
    id: "3",
    title: "Understanding Threat Categories",
    description:
      "Learn about different types of threats and how DNS filtering protects against them",
    category: "security",
    difficulty: "beginner",
    readTime: "6 min read",
    tags: ["security", "threats", "protection"],
    isNew: true,
  },
  {
    id: "4",
    title: "Advanced Policy Configuration",
    description:
      "Deep dive into advanced policy features and customization options",
    category: "policies",
    difficulty: "advanced",
    readTime: "15 min read",
    tags: ["advanced", "customization", "features"],
  },
  {
    id: "5",
    title: "Troubleshooting Common Issues",
    description: "Solutions to frequently encountered problems and their fixes",
    category: "troubleshooting",
    difficulty: "intermediate",
    readTime: "10 min read",
    tags: ["troubleshooting", "problems", "solutions"],
    isPopular: true,
  },
  {
    id: "6",
    title: "Security Best Practices",
    description:
      "Industry best practices for maintaining a secure DNS environment",
    category: "security",
    difficulty: "intermediate",
    readTime: "12 min read",
    tags: ["best-practices", "security", "compliance"],
  },
  {
    id: "7",
    title: "Integration with Existing Systems",
    description:
      "How to integrate DNS filtering with your current network infrastructure",
    category: "advanced",
    difficulty: "advanced",
    readTime: "20 min read",
    tags: ["integration", "networking", "infrastructure"],
  },
  {
    id: "8",
    title: "Monitoring and Analytics",
    description:
      "Understanding your security metrics and threat intelligence data",
    category: "advanced",
    difficulty: "intermediate",
    readTime: "8 min read",
    tags: ["monitoring", "analytics", "metrics"],
  },
];

const helpCategories: HelpCategory[] = [
  {
    id: "getting-started",
    name: "Getting Started",
    description: "Essential guides for new users",
    icon: <BookOpen className="h-6 w-6" />,
    articleCount: 2,
    color: "bg-blue-500",
  },
  {
    id: "policies",
    name: "Policies",
    description: "Policy creation and management",
    icon: <FileText className="h-6 w-6" />,
    articleCount: 2,
    color: "bg-green-500",
  },
  {
    id: "security",
    name: "Security",
    description: "Security features and best practices",
    icon: <HelpCircle className="h-6 w-6" />,
    articleCount: 2,
    color: "bg-red-500",
  },
  {
    id: "troubleshooting",
    name: "Troubleshooting",
    description: "Common issues and solutions",
    icon: <AlertCircle className="h-6 w-6" />,
    articleCount: 1,
    color: "bg-yellow-500",
  },
  {
    id: "advanced",
    name: "Advanced",
    description: "Advanced features and integration",
    icon: <Lightbulb className="h-6 w-6" />,
    articleCount: 2,
    color: "bg-purple-500",
  },
];

const videoTutorials = [
  {
    id: "1",
    title: "Quick Start Guide",
    duration: "3:45",
    thumbnail: "🎥",
    description: "Get up and running in under 4 minutes",
  },
  {
    id: "2",
    title: "Policy Management",
    duration: "8:20",
    thumbnail: "🎥",
    description: "Learn how to create and manage policies",
  },
  {
    id: "3",
    title: "Security Dashboard",
    duration: "5:15",
    thumbnail: "🎥",
    description: "Understanding your security metrics",
  },
];

const faqs = [
  {
    question: "How does DNS filtering work?",
    answer:
      "DNS filtering works by intercepting DNS queries and checking them against a database of known malicious or unwanted domains. When a query matches a blocked domain, the request is redirected to a safe page or blocked entirely.",
  },
  {
    question: "Can I whitelist trusted domains?",
    answer:
      "Yes, you can create whitelist policies that allow access to specific domains even if they might be blocked by other policies. This is useful for business-critical websites.",
  },
  {
    question: "How often are threat databases updated?",
    answer:
      "Our threat databases are updated in real-time as new threats are discovered. We maintain partnerships with leading security organizations to ensure the most current protection.",
  },
  {
    question: "What happens if a legitimate site is blocked?",
    answer:
      "If a legitimate site is accidentally blocked, you can submit a review request through the dashboard. Our team will review and whitelist legitimate sites within 24 hours.",
  },
  {
    question: "Can I use this with my existing firewall?",
    answer:
      "Yes, DNS filtering works alongside traditional firewalls and can be integrated with most network security solutions. It provides an additional layer of protection at the DNS level.",
  },
];

export default function Help() {
  const { data: organization } = useUserOrganization();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>("all");

  if (!organization) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <HelpCircle className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl font-bold mb-2">Organization Required</h2>
          <p className="text-muted-foreground">
            Please set up your organization to access help resources.
          </p>
        </div>
      </div>
    );
  }

  const filteredArticles = helpArticles.filter((article) => {
    const matchesSearch =
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.tags.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesCategory =
      selectedCategory === "all" || article.category === selectedCategory;
    const matchesDifficulty =
      selectedDifficulty === "all" || article.difficulty === selectedDifficulty;

    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "beginner":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "intermediate":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case "advanced":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
  };

  const getCategoryColor = (categoryId: string) => {
    const category = helpCategories.find((c) => c.id === categoryId);
    return category?.color || "bg-gray-500";
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SiteHeader />
      <SidebarInset>
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Help Center</h1>
              <p className="text-muted-foreground">
                Get help and learn how to use {organization.name}'s DNS security
                platform
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline">
                <MessageSquare className="h-4 w-4 mr-2" />
                Contact Support
              </Button>
              <Button>
                <ExternalLink className="h-4 w-4 mr-2" />
                View Documentation
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-2xl">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Search help articles, tutorials, and FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 text-lg py-6"
            />
          </div>
          {/* Main Content Tabs */}
          <Tabs defaultValue="articles" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="articles">Help Articles</TabsTrigger>
              <TabsTrigger value="videos">Video Tutorials</TabsTrigger>
              <TabsTrigger value="faq">FAQ</TabsTrigger>
            </TabsList>

            {/* Help Articles Tab */}
            <TabsContent value="articles" className="space-y-6">
              {/* Filters */}
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex gap-2">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-3 py-2 border rounded-md text-sm bg-background"
                  >
                    <option value="all">All Categories</option>
                    {helpCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>

                  <select
                    value={selectedDifficulty}
                    onChange={(e) => setSelectedDifficulty(e.target.value)}
                    className="px-3 py-2 border rounded-md text-sm bg-background"
                  >
                    <option value="all">All Levels</option>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>

                <Badge variant="outline">
                  {filteredArticles.length} articles found
                </Badge>
              </div>

              {/* Articles List */}
              <div className="space-y-4">
                {filteredArticles.map((article) => (
                  <Card
                    key={article.id}
                    className="hover:shadow-lg transition-shadow"
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <CardTitle className="text-lg">
                              {article.title}
                            </CardTitle>
                            {article.isPopular && (
                              <Badge
                                variant="default"
                                className="bg-yellow-500"
                              >
                                <Star className="h-3 w-3 mr-1" />
                                Popular
                              </Badge>
                            )}
                            {article.isNew && (
                              <Badge variant="default" className="bg-green-500">
                                New
                              </Badge>
                            )}
                          </div>
                          <CardDescription className="text-base">
                            {article.description}
                          </CardDescription>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Badge
                            variant="outline"
                            className={getDifficultyColor(article.difficulty)}
                          >
                            {article.difficulty}
                          </Badge>
                          <Badge variant="secondary">
                            <Clock className="h-3 w-3 mr-1" />
                            {article.readTime}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-1">
                          {article.tags.map((tag, index) => (
                            <Badge
                              key={index}
                              variant="outline"
                              className="text-xs"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline">
                            <Bookmark className="h-4 w-4 mr-2" />
                            Bookmark
                          </Button>
                          <Button size="sm">
                            <BookOpen className="h-4 w-4 mr-2" />
                            Read Article
                            <ChevronRight className="h-4 w-4 ml-2" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Video Tutorials Tab */}
            <TabsContent value="videos" className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {videoTutorials.map((video) => (
                  <Card
                    key={video.id}
                    className="hover:shadow-lg transition-shadow"
                  >
                    <CardHeader>
                      <div className="text-center mb-4">
                        <div className="text-4xl mb-2">{video.thumbnail}</div>
                        <CardTitle className="text-lg">{video.title}</CardTitle>
                      </div>
                      <CardDescription className="text-center">
                        {video.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-4">
                        <Clock className="h-4 w-4" />
                        <span className="text-sm text-muted-foreground">
                          {video.duration}
                        </span>
                      </div>
                      <Button className="w-full">
                        <Play className="h-4 w-4 mr-2" />
                        Watch Video
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* FAQ Tab */}
            <TabsContent value="faq" className="space-y-6">
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <Card
                    key={index}
                    className="hover:shadow-lg transition-shadow"
                  >
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <HelpCircle className="h-5 w-5 text-blue-500" />
                        {faq.question}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{faq.answer}</p>
                      <div className="flex gap-2 mt-4">
                        <Button size="sm" variant="outline">
                          <MessageCircle className="h-4 w-4 mr-2" />
                          Still need help?
                        </Button>
                        <Button size="sm" variant="outline">
                          <ThumbsUp className="h-4 w-4 mr-2" />
                          Helpful
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
